import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { 
    collection, 
    addDoc, 
    onSnapshot, 
    deleteDoc, 
    doc,
    query,
    orderBy 
} from 'firebase/firestore';
import './Music.css';

const Music = () => {
    const { currentUser, isAdmin } = useAuth();
    const { showToast } = useToast();
    const [songs, setSongs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        title: '',
        artist: '',
        url: ''
    });
    const [submitting, setSubmitting] = useState(false);

    // Player state
    const [showPlayer, setShowPlayer] = useState(false);
    const [currentSong, setCurrentSong] = useState(null);
    const [currentSongIndex, setCurrentSongIndex] = useState(-1);
    const [isPlaying, setIsPlaying] = useState(false);

    const audioRef = useRef(null);

    useEffect(() => {
        loadSongs();
    }, []);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const loadSongs = () => {
        const songsQuery = query(
            collection(db, 'songs'),
            orderBy('addedAt', 'desc')
        );

        const unsubscribe = onSnapshot(songsQuery, (snapshot) => {
            const songsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSongs(songsData);
            setLoading(false);
        }, (error) => {
            console.error('Error loading songs:', error);
            showToast('Error loading songs', 'error');
            setLoading(false);
        });

        return unsubscribe;
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const getPlatformInfo = (url) => {
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
            return { name: 'YouTube', icon: 'fab fa-youtube', color: '#FF0000', badgeClass: 'platform-youtube' };
        } else if (url.includes('boomplay.com')) {
            return { name: 'Boomplay', icon: 'fas fa-music', color: '#0066CC', badgeClass: 'platform-boomplay' };
        } else if (url.includes('spotify.com')) {
            return { name: 'Spotify', icon: 'fab fa-spotify', color: '#1DB954', badgeClass: 'platform-spotify' };
        }
        return { name: 'Other', icon: 'fas fa-link', color: '#1034A6', badgeClass: 'platform-other' };
    };

    const getYouTubeVideoId = (url) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const getYouTubeThumbnail = (videoId, quality = 'mqdefault') =>
        `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;

    const validateForm = () => {
        if (!formData.title.trim()) { showToast('Please enter a song title', 'error'); return false; }
        if (!formData.artist.trim()) { showToast('Please enter an artist name', 'error'); return false; }
        if (!formData.url.trim()) { showToast('Please paste a music link', 'error'); return false; }
        const platform = getPlatformInfo(formData.url);
        if (platform.name === 'Other') { showToast('Only links from YouTube, Boomplay, or Spotify are supported', 'error'); return false; }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentUser) { showToast('You must be logged in to add songs', 'error'); return; }
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            const platform = getPlatformInfo(formData.url);
            const songData = {
                title: formData.title.trim(),
                artist: formData.artist.trim(),
                url: formData.url.trim(),
                platform: platform.name,
                addedAt: new Date().toISOString(),
                addedBy: currentUser.uid,
                addedByEmail: currentUser.email
            };
            await addDoc(collection(db, 'songs'), songData);
            setFormData({ title: '', artist: '', url: '' });
            showToast('Song added successfully!', 'success');
        } catch (error) {
            console.error('Error adding song:', error);
            showToast('Error adding song: ' + error.message, 'error');
        } finally { setSubmitting(false); }
    };

    const handleDelete = async (songId) => {
        if (!isAdmin) { showToast('Only admins can delete songs', 'error'); return; }
        if (!window.confirm('Are you sure you want to delete this song?')) return;
        try {
            await deleteDoc(doc(db, 'songs', songId));
            showToast('Song deleted successfully!', 'success');
        } catch (error) {
            console.error('Error deleting song:', error);
            showToast('Error deleting song', 'error');
        }
    };

    const playSong = (song, index) => {
        setCurrentSong(song);
        setCurrentSongIndex(index);
        setShowPlayer(true);
        setIsPlaying(true);
        if (audioRef.current) audioRef.current.pause();
    };

    const closePlayer = () => {
        setShowPlayer(false);
        setCurrentSong(null);
        setIsPlaying(false);
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };

    const playNext = () => { if (currentSongIndex < songs.length - 1) playSong(songs[currentSongIndex + 1], currentSongIndex + 1); };
    const playPrevious = () => { if (currentSongIndex > 0) playSong(songs[currentSongIndex - 1], currentSongIndex - 1); };

    if (loading) return (
        <div className="music-loading">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading music library...</p>
        </div>
    );

    return (
        <div className="music-page">
            <h1 className="music-page-title">Ministry Music Collection</h1>

            {/* Music Grid */}
            <div className="music-grid">
                {songs.length === 0 ? (
                    <div className="music-empty-state">
                        <i className="fas fa-music"></i>
                        <h3>No Songs Added Yet</h3>
                        <p>Add your first song using the form below</p>
                    </div>
                ) : songs.map((song, index) => {
                    const platform = getPlatformInfo(song.url);
                    const videoId = song.platform === 'YouTube' ? getYouTubeVideoId(song.url) : null;

                    return (
                        <div key={song.id} className={`music-card ${currentSong?.id === song.id ? 'currently-playing' : ''}`}>
                            <div className="music-thumbnail-container" onClick={() => playSong(song, index)}>
                                {videoId ? (
                                    <img src={getYouTubeThumbnail(videoId)} alt={song.title} className="music-thumbnail" loading="lazy" />
                                ) : (
                                    <div className="music-thumbnail-default" style={{ background: platform.color }}>
                                        <i className={platform.icon}></i>
                                    </div>
                                )}
                            </div>
                            <div className="music-content">
                                <h3 className="music-title">{song.title}</h3>
                                <p className="music-artist">{song.artist}</p>
                                <div className="music-footer">
                                    <div className={`music-platform-badge ${platform.badgeClass}`}>
                                        <i className={platform.icon}></i> {platform.name}
                                    </div>
                                    <div className="music-actions">
                                        <button className="music-play-btn" onClick={() => playSong(song, index)}>
                                            <i className="fas fa-play"></i> Play
                                        </button>
                                        {isAdmin && (
                                            <button className="music-delete-btn" onClick={() => handleDelete(song.id)}>
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Add Song Form */}
            {currentUser && (
                <div className="music-upload-section">
                    <h2>Add New Song</h2>
                    <form onSubmit={handleSubmit}>
                        <input type="text" id="title" placeholder="Song Title" value={formData.title} onChange={handleInputChange} required />
                        <input type="text" id="artist" placeholder="Artist" value={formData.artist} onChange={handleInputChange} required />
                        <input type="url" id="url" placeholder="YouTube, Boomplay, Spotify link" value={formData.url} onChange={handleInputChange} required />
                        <button type="submit" disabled={submitting}>{submitting ? 'Adding...' : 'Add Song'}</button>
                    </form>
                </div>
            )}

            {/* Music Player Modal */}
            {showPlayer && currentSong && (
                <div className="music-player-modal" onClick={closePlayer}>
                    <div className="music-player-container" onClick={e => e.stopPropagation()}>
                        <div className="music-player-header">
                            <h3>Now Playing</h3>
                            <button onClick={closePlayer}><i className="fas fa-times"></i></button>
                        </div>

                        <div className="music-player-body">
                            <div className="music-player-info">
                                <div className="music-player-thumbnail">
                                    {currentSong.platform === 'YouTube' ? (
                                        <img src={getYouTubeThumbnail(getYouTubeVideoId(currentSong.url), 'hqdefault')} alt={currentSong.title} />
                                    ) : (
                                        <div className="music-player-thumbnail-default" style={{ background: getPlatformInfo(currentSong.url).color }}>
                                            <i className={getPlatformInfo(currentSong.url).icon}></i>
                                        </div>
                                    )}
                                </div>
                                <div className="music-player-details">
                                    <h2>{currentSong.title}</h2>
                                    <p>{currentSong.artist}</p>
                                    <div className={`music-player-platform-tag ${getPlatformInfo(currentSong.url).badgeClass}`}>
                                        <i className={getPlatformInfo(currentSong.url).icon}></i> {getPlatformInfo(currentSong.url).name}
                                    </div>
                                </div>
                            </div>

                            {/* React iframe player */}
                            <div className="music-player-iframe-container">
                                {currentSong.platform === 'YouTube' && (
                                    <iframe
                                        width="100%"
                                        height="300"
                                        src={`https://www.youtube.com/embed/${getYouTubeVideoId(currentSong.url)}?autoplay=1&controls=1&modestbranding=1&rel=0&playsinline=1`}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        title={currentSong.title}
                                        style={{ borderRadius: '10px', marginTop: '20px' }}
                                    />
                                )}
                            </div>

                            <div className="music-player-controls">
                                <button onClick={playPrevious} disabled={currentSongIndex === 0}><i className="fas fa-step-backward"></i></button>
                                <button onClick={() => {
                                    if (audioRef.current) {
                                        if (isPlaying) { audioRef.current.pause(); } 
                                        else { audioRef.current.play(); }
                                        setIsPlaying(!isPlaying);
                                    }
                                }}><i className={`fas fa-${isPlaying ? 'pause' : 'play'}`}></i></button>
                                <button onClick={playNext} disabled={currentSongIndex === songs.length - 1}><i className="fas fa-step-forward"></i></button>

                                <a href={currentSong.url} target="_blank" rel="noopener noreferrer"><i className="fas fa-external-link-alt"></i> Open in Original Platform</a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Music;
